// Rediseño · Bloque E — «Nuevo producto» (P-029), decidido sin React para poder probarlo sin pantalla:
// los cinco pasos y su orden, qué se valida en cada uno, qué dice cada respuesta, qué importe se cobra
// y qué se manda al servidor. Nada de aquí habla con el servidor ni decide permisos: el alta es la de
// siempre (`POST /api/other-sales/products`, `buildProductPayload`) y el servidor sigue siendo quien
// valida de verdad.
import { ApiError } from '../api/client';
import type { Recipe } from '../types/inventory';
import type { CostMode, CreateOtherSaleProductInput, OtherSaleTaxMode } from '../types/otherSales';
import type { UserRole } from '../types/auth';
import { formatMoney } from './costFormatting';
import { buildProductPayload, emptyProductForm } from './otherSaleProductForm';
import { eligibleRecipeOptions } from './otherSaleRecipeAssignment';
import { calculateOtherSaleTaxBreakdown } from './otherSaleTax';

/** Los cinco pasos de P-029, en su orden. `REVIEW` es la revisión «Así quedará». */
export const PRODUCT_STEPS = ['name', 'price', 'stores', 'recipe', 'tax'] as const;
export type ProductStep = (typeof PRODUCT_STEPS)[number];
export type ProductScreen = ProductStep | 'review';

/** El importe de «Impuestos incluidos» es fijo en el contrato: 16 % (otherSaleTax.ts, B0-07). */
export const INCLUDED_TAX_PERCENT = 16;

/** Topes reales: el nombre, el del esquema (120); el precio, la columna Decimal(14,2) (12 enteros). */
export const PRODUCT_NAME_MAX = 120;
const PRICE_INTEGER_DIGITS = 12;
const PERCENT_INTEGER_DIGITS = 3;

export type TaxChoice = OtherSaleTaxMode;

export interface ProductDraft {
  name: string;
  /** Lo que la persona escribió, con punto o coma. */
  price: string;
  /** «Todas mis tiendas». Para el OWNER es la lista vacía (todas, también las futuras); para un MANAGER, sus tiendas una por una. */
  allStores: boolean;
  storeIds: string[];
  /**
   * P-071: cómo quiere llevar el costo. null = todavía no contestó. NONE «Solo quiero vender»,
   * APPROXIMATE «Sé cuánto me cuesta aprox.» (con `approximateCost`), RECIPE «Quiero control completo»
   * (con `recipeId`). Un solo modo a la vez: lo que no corresponde al modo elegido no viaja.
   */
  costMode: CostMode | null;
  /** Lo que la persona escribió como costo aproximado por unidad, con punto o coma. */
  approximateCost: string;
  recipeId: string | null;
  /** null = todavía no eligió. */
  tax: TaxChoice | null;
  taxPercent: string;
}

/**
 * Cómo empieza el asistente. Lo que el alta de siempre dejaba por defecto se conserva donde valía: el
 * OWNER sin tocar nada creaba el producto para todas sus tiendas, así que empieza con «Todas mis
 * tiendas»; un MANAGER no tenía un valor válido (el servidor rechaza la lista vacía) y elige. La receta y
 * el impuesto son preguntas de P-029 que se contestan con un toque; no se contestan solas.
 */
export function initialDraft(role: UserRole | undefined): ProductDraft {
  return { name: '', price: '', allStores: role === 'OWNER', storeIds: [], costMode: null, approximateCost: '', recipeId: null, tax: null, taxPercent: '' };
}

// ── Captura ─────────────────────────────────────────────────────────────────────────────────────

/**
 * Lo que se escribe en el precio y en el porcentaje. Se quita solo lo que nunca forma parte de un número
 * (letras, «$», espacios); el signo, los separadores y los dígitos de más se CONSERVAN para que la
 * validación los rechace a la vista. Recortarlos convertía una entrada inválida en otra válida —«-5» en
 * 5, «1000» % en 100—: lo encontró la revisión independiente.
 */
export function sanitizePrice(raw: string): string {
  return raw.replace(/[^\d.,-]/g, '');
}

export function sanitizePercent(raw: string): string {
  return raw.replace(/[^\d.,-]/g, '');
}

/** ¿Está escrito como se acepta? Hasta `integerDigits` enteros, un separador y hasta dos decimales, sin signo. */
function wellFormed(text: string, integerDigits: number): boolean {
  return new RegExp(`^\\d{0,${integerDigits}}([.,]\\d{0,2})?$`).test(text.trim()) && Number.isFinite(parseAmount(text));
}

/** Un número escrito con punto o coma; NaN si no hay número. */
export function parseAmount(text: string): number {
  const normalized = text.trim().replace(',', '.');
  if (normalized === '' || normalized === '.') return Number.NaN;
  return Number(normalized);
}

// ── Validación (la de siempre, con las palabras de P-029) ─────────────────────────────────────────

/** Lo que la validación necesita saber además de las respuestas: quién crea y en qué tiendas puede. */
export interface ProductContext { role: UserRole | undefined; stores: readonly StoreOption[] }

/**
 * El primer problema del paso, o null. Las reglas son las del alta de siempre (`validateProductForm`):
 * precio presente y no negativo —cero se puede—, y con «Agregar impuestos» un porcentaje de 0 a 100.
 * P-029 pedía un precio mayor que cero; no se endurece lo que el producto acepta. El formato es el de
 * P-029 y el de la columna `Decimal(14,2)`: hasta doce enteros y dos decimales; lo que no cabe se
 * rechaza a la vista, no se recorta.
 *
 * Tiendas: «Todas mis tiendas» vale para el OWNER aunque aún no tenga ninguna (la lista vacía las
 * incluye todas, también las futuras); para un MANAGER solo si tiene alguna, porque «todas» son SUS
 * tiendas, una por una.
 */
export function stepProblem(step: ProductStep, draft: ProductDraft, context: ProductContext): string | null {
  switch (step) {
    case 'name':
      return draft.name.trim() ? null : 'Escribe el nombre del producto.';
    case 'price':
      // Doce enteros y dos decimales: lo que cabe en la columna Decimal(14,2). Sin signo: no hay precio negativo.
      return wellFormed(draft.price, PRICE_INTEGER_DIGITS) ? null : 'Escribe un precio válido.';
    case 'stores':
      return storeIdsForPayload(draft, context.role, context.stores).length > 0 || draft.allStores && context.role === 'OWNER'
        ? null
        : 'Elige al menos una tienda.';
    case 'recipe': {
      // P-071: el paso del costo. Con receta, elegir una; con costo aproximado, un número mayor que cero
      // con el formato del precio (hasta doce enteros y dos decimales). «0» no es un costo conocido.
      if (draft.costMode === null) return 'Elige una opción.';
      if (draft.costMode === 'RECIPE') return draft.recipeId ? null : 'Elige una receta.';
      if (draft.costMode === 'APPROXIMATE') {
        if (draft.approximateCost.trim() === '') return 'Escribe el costo aproximado.';
        return wellFormed(draft.approximateCost, PRICE_INTEGER_DIGITS) && parseAmount(draft.approximateCost) > 0 ? null : 'Escribe un costo mayor que cero.';
      }
      return null;
    }
    case 'tax': {
      if (draft.tax === null) return 'Elige una opción.';
      if (draft.tax !== 'CUSTOM') return null;
      if (draft.taxPercent.trim() === '') return 'Escribe el porcentaje.';
      const percent = parseAmount(draft.taxPercent);
      return wellFormed(draft.taxPercent, PERCENT_INTEGER_DIGITS) && percent <= 100 ? null : 'Escribe un porcentaje de 0 a 100.';
    }
    default:
      return null;
  }
}

/**
 * Dónde va el error de un paso: los de «¿Cuál receta?» y «¿Qué porcentaje?» van junto a su sub-bloque,
 * como en P-029; los demás, bajo la pregunta.
 */
export function problemPlace(step: ProductStep, problem: string): 'question' | 'sub' {
  // P-071: en el paso del costo, todo lo que no sea «Elige una opción.» es de su sub-bloque (la receta o el costo).
  if (step === 'recipe' && problem !== 'Elige una opción.') return 'sub';
  if (step === 'tax' && problem !== 'Elige una opción.') return 'sub';
  return 'question';
}

// ── Avance (P-029: `next`, `back`, `nextTarget`) ────────────────────────────────────────────────

/** A dónde lleva «Continuar»: al primer paso aún sin confirmar; si no queda ninguno, a la revisión. */
export function nextScreen(current: ProductScreen, confirmed: ReadonlySet<ProductStep>): ProductScreen {
  return PRODUCT_STEPS.find((step) => step !== current && !confirmed.has(step)) ?? 'review';
}

/** El rótulo del botón: «Revisar producto» cuando lo que sigue es la revisión. */
export function continueLabel(current: ProductStep, confirmed: ReadonlySet<ProductStep>): string {
  return nextScreen(current, confirmed) === 'review' ? 'Revisar producto' : 'Continuar';
}

/** «Atrás»: un paso hacia atrás; de la revisión, al impuesto; del primero, fuera del asistente (null). */
export function previousScreen(current: ProductScreen): ProductScreen | null {
  if (current === 'review') return 'tax';
  const index = PRODUCT_STEPS.indexOf(current);
  return index > 0 ? PRODUCT_STEPS[index - 1] : null;
}

/** «Paso N de 5» o «Revisión». */
export function stepPill(screen: ProductScreen): string {
  return screen === 'review' ? 'Revisión' : `Paso ${PRODUCT_STEPS.indexOf(screen) + 1} de ${PRODUCT_STEPS.length}`;
}

export type ProgressMark = 'done' | 'current' | 'todo';

/** Los cinco segmentos: hecho si se confirmó y no es el actual; actual; o pendiente. */
export function progressMarks(screen: ProductScreen, confirmed: ReadonlySet<ProductStep>): ProgressMark[] {
  return PRODUCT_STEPS.map((step) => {
    if (screen === 'review') return 'done';
    if (step === screen) return 'current';
    return confirmed.has(step) ? 'done' : 'todo';
  });
}

/** La primera respuesta que no pasa, para llevar ahí a la persona antes de crear (P-029 `create`). */
export function firstProblem(draft: ProductDraft, context: ProductContext): { step: ProductStep; problem: string } | null {
  for (const step of PRODUCT_STEPS) {
    const problem = stepProblem(step, draft, context);
    if (problem) return { step, problem };
  }
  return null;
}

// ── Tiendas ─────────────────────────────────────────────────────────────────────────────────────

export interface StoreOption { id: string; name: string; isActive: boolean }

/** Tocar «Todas mis tiendas»: si estaba encendido se apaga sin ninguna; si no, se enciende. */
export function toggleAllStores(draft: ProductDraft): ProductDraft {
  return draft.allStores ? { ...draft, allStores: false, storeIds: [] } : { ...draft, allStores: true, storeIds: [] };
}

/**
 * Tocar una tienda. Con «Todas» encendido, quitarla deja elegidas las demás. Si al marcar quedan todas,
 * se enciende «Todas mis tiendas», como en P-029, donde el interruptor es «todas están elegidas».
 */
export function toggleStore(draft: ProductDraft, storeId: string, available: readonly StoreOption[]): ProductDraft {
  const current = draft.allStores ? available.map((store) => store.id) : draft.storeIds;
  const next = current.includes(storeId) ? current.filter((id) => id !== storeId) : [...current, storeId];
  if (available.length > 0 && available.every((store) => next.includes(store.id))) return { ...draft, allStores: true, storeIds: [] };
  return { ...draft, allStores: false, storeIds: next };
}

export function isStoreChosen(draft: ProductDraft, storeId: string): boolean {
  return draft.allStores || draft.storeIds.includes(storeId);
}

/**
 * Las tiendas que viajan al servidor. «Todas mis tiendas» del OWNER es la lista vacía, que el contrato
 * lee como todas, también las que agregue después. La de un MANAGER es la lista de SUS tiendas, una por
 * una: para él la lista vacía no es «todas» (B0-02, FUTURE_UPGRADES #40) y el servidor la rechaza. Sin
 * ids repetidos, en el orden de la lista.
 */
export function storeIdsForPayload(draft: ProductDraft, role: UserRole | undefined, available: readonly StoreOption[]): string[] {
  if (draft.allStores) return role === 'OWNER' ? [] : available.map((store) => store.id);
  return available.map((store) => store.id).filter((id) => draft.storeIds.includes(id));
}

/** «Centro», «Centro y Norte», «Centro, Malecón y Norte»; «Todas mis tiendas». */
export function storesText(draft: ProductDraft, available: readonly StoreOption[]): string {
  if (draft.allStores) return 'Todas mis tiendas';
  const names = available.filter((store) => draft.storeIds.includes(store.id)).map((store) => store.name);
  if (names.length === 0) return '—';
  return names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} y ${names[names.length - 1]}`;
}

// ── Recetas ─────────────────────────────────────────────────────────────────────────────────────

export interface RecipeChoice { id: string; name: string; selectable: boolean; note: string | null }

/**
 * Las recetas que se ofrecen: las ACTIVAS, como en el alta de siempre (`eligibleRecipeOptions`). Una
 * activa sin insumos se muestra, pero no se puede elegir: el servidor la rechaza al crear
 * (OTHER_SALE_CONSUMPTION_RECIPE_EMPTY), así que ofrecerla sería ofrecer un error.
 */
export function recipeChoices(recipes: readonly Recipe[]): RecipeChoice[] {
  const byId = new Map(recipes.map((recipe) => [recipe.id, recipe]));
  return eligibleRecipeOptions([...recipes], null).map((option) => {
    const empty = (byId.get(option.id)?.componentsCount ?? 0) === 0;
    return { id: option.id, name: option.label, selectable: !empty, note: empty ? 'Todavía no tiene insumos' : null };
  });
}

/** Sin acentos ni mayúsculas, como el buscador del punto de venta. */
export function normalizeSearch(text: string): string {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
}

export function filterRecipes(choices: readonly RecipeChoice[], query: string): RecipeChoice[] {
  const wanted = normalizeSearch(query);
  return wanted ? choices.filter((choice) => normalizeSearch(choice.name).includes(wanted)) : [...choices];
}

// ── Costo (P-071) ───────────────────────────────────────────────────────────────────────────────

/** Las tres formas de llevar el costo, en el orden del brief, con su línea de apoyo. */
export const COST_OPTIONS: ReadonlyArray<{ value: CostMode; label: string; hint: string }> = [
  { value: 'NONE', label: 'Solo quiero vender', hint: 'Puedes empezar ahora. Más adelante puedes agregar el costo.' },
  { value: 'APPROXIMATE', label: 'Sé cuánto me cuesta aprox.', hint: 'Un costo por pieza, sin insumos. No descuenta inventario.' },
  { value: 'RECIPE', label: 'Quiero control completo', hint: 'Con una receta: calcula el costo y descuenta los insumos al vender.' },
];

/** Cómo se dice en la revisión («Así quedará»): nunca margen ni ganancia aquí. */
export function costText(draft: ProductDraft, recipes: readonly Recipe[]): string {
  if (draft.costMode === 'APPROXIMATE') {
    const amount = parseAmount(draft.approximateCost);
    return Number.isFinite(amount) ? `Aproximadamente ${formatMoney(String(amount))}` : 'Aproximadamente —';
  }
  if (draft.costMode === 'RECIPE') {
    const name = recipes.find((recipe) => recipe.id === draft.recipeId)?.name;
    return name ? `Según receta · ${name}` : 'Según receta';
  }
  return 'Sin definir por ahora';
}

// ── Impuestos (B0-07) ───────────────────────────────────────────────────────────────────────────

/** Las tres opciones con los nombres de B0-07. */
export const TAX_OPTIONS: ReadonlyArray<{ value: TaxChoice; label: string }> = [
  { value: 'NONE', label: 'Sin impuestos' },
  { value: 'INCLUDED', label: 'Impuestos incluidos' },
  { value: 'CUSTOM', label: 'Agregar impuestos' },
];

export function formatPercent(text: string): string {
  const value = parseAmount(text);
  if (!Number.isFinite(value)) return '';
  return `${Number(value.toFixed(2))} %`;
}

/** La línea de apoyo de cada opción. «Incluidos» dice el 16 %, que es fijo; «Agregar» dice que se suma. */
export function taxHint(choice: TaxChoice, price: string): string {
  if (choice === 'NONE') return 'No se le aplica impuesto';
  if (choice === 'INCLUDED') {
    const amount = parseAmount(price);
    return Number.isFinite(amount) ? `Los ${formatMoney(String(amount))} ya incluyen el ${INCLUDED_TAX_PERCENT} %` : `El precio ya incluye el ${INCLUDED_TAX_PERCENT} %`;
  }
  return 'Tú escribes el porcentaje y se suma al precio';
}

export function taxText(draft: ProductDraft): string {
  if (draft.tax === 'NONE') return 'Sin impuestos';
  if (draft.tax === 'INCLUDED') return `Impuestos incluidos · ${INCLUDED_TAX_PERCENT} %`;
  if (draft.tax === 'CUSTOM') return `Agregar impuestos · ${formatPercent(draft.taxPercent)}`;
  return '—';
}

/**
 * Lo que se cobra por una pieza, con el mismo cálculo que el punto de venta (B0-07: el protagonista de
 * la revisión es el importe final). Con «Agregar impuestos» el precio capturado es el subtotal y el
 * porcentaje se suma encima; con «Incluidos» y «Sin impuestos», es el precio.
 */
export function finalUnitAmount(draft: ProductDraft): number {
  const price = parseAmount(draft.price);
  const percent = draft.tax === 'CUSTOM' ? parseAmount(draft.taxPercent) : null;
  return calculateOtherSaleTaxBreakdown(Number.isFinite(price) ? price : 0, draft.tax ?? 'NONE', percent).totalAmount;
}

export function priceText(draft: ProductDraft): string {
  const price = parseAmount(draft.price);
  return Number.isFinite(price) ? formatMoney(String(price)) : '—';
}

// ── El envío ────────────────────────────────────────────────────────────────────────────────────

/**
 * El cuerpo del alta, armado con el `buildProductPayload` de siempre: los campos que P-029 no pregunta
 * conservan el valor del alta anterior (orden de visualización 0). El estado activo no viaja: el
 * servidor lo crea activo («Disponible para vender»). P-071: el modo de costo viaja siempre y solo lo
 * que ese modo usa (la receta o el costo aproximado); lo demás, null.
 */
export function productPayload(draft: ProductDraft, role: UserRole | undefined, available: readonly StoreOption[]): CreateOtherSaleProductInput {
  const base = emptyProductForm();
  return buildProductPayload({
    ...base,
    name: draft.name,
    price: String(parseAmount(draft.price)),
    taxMode: draft.tax ?? 'NONE',
    taxPercentage: draft.tax === 'CUSTOM' ? String(parseAmount(draft.taxPercent)) : '',
    costMode: draft.costMode ?? 'NONE',
    recipeId: draft.costMode === 'RECIPE' ? draft.recipeId ?? '' : '',
    approximateUnitCost: draft.costMode === 'APPROXIMATE' ? String(parseAmount(draft.approximateCost)) : '',
    storeIds: storeIdsForPayload(draft, role, available),
  });
}

// ── Respuestas del servidor ─────────────────────────────────────────────────────────────────────

export interface CreateFailure {
  /** El paso al que se lleva a la persona, o la revisión si no es de un paso. */
  screen: ProductScreen;
  message: string;
  /** No se sabe si el producto se creó (sin red o error del servidor). */
  uncertain: boolean;
}

const STEP_BY_CODE: Record<string, { step: ProductStep; message: string }> = {
  OTHER_SALE_PRODUCT_DUPLICATE: { step: 'name', message: 'Ya existe un producto con ese nombre.' },
  OTHER_SALE_PRODUCT_ALL_STORES_OWNER_ONLY: { step: 'stores', message: 'Solo el dueño puede dejar un producto en todas las tiendas. Elige tus tiendas.' },
  OTHER_SALE_STORE_ACCESS_DENIED: { step: 'stores', message: 'No tienes acceso a una de esas tiendas. Elige otras.' },
  OTHER_SALE_PRODUCT_STORE_NOT_FOUND: { step: 'stores', message: 'Una de esas tiendas ya no existe. Elige otras.' },
  OTHER_SALE_PRODUCT_RECIPE_NOT_FOUND: { step: 'recipe', message: 'Esa receta ya no existe. Elige otra.' },
  OTHER_SALE_CONSUMPTION_RECIPE_INACTIVE: { step: 'recipe', message: 'Esa receta está desactivada. Elige otra o actívala en Recetas y costos.' },
  OTHER_SALE_CONSUMPTION_RECIPE_EMPTY: { step: 'recipe', message: 'Esa receta todavía no tiene insumos. Elige otra o agrégaselos en Recetas y costos.' },
  OTHER_SALE_CONSUMPTION_RECIPE_INVALID_QUANTITY: { step: 'recipe', message: 'Esa receta tiene un insumo con una cantidad que no sirve. Elige otra o corrígela en Recetas y costos.' },
  OTHER_SALE_CONSUMPTION_UNIT_INCOMPATIBLE: { step: 'recipe', message: 'Esa receta tiene un insumo con unidades que no se pueden convertir. Elige otra o corrígela en Recetas y costos.' },
  // P-071: los rechazos del modo de costo vuelven al paso del costo.
  OTHER_SALE_PRODUCT_APPROXIMATE_COST_REQUIRED: { step: 'recipe', message: 'Escribe el costo aproximado.' },
  OTHER_SALE_PRODUCT_APPROXIMATE_COST_INVALID: { step: 'recipe', message: 'Escribe un costo mayor que cero.' },
  OTHER_SALE_PRODUCT_RECIPE_REQUIRED: { step: 'recipe', message: 'Elige una receta.' },
  OTHER_SALE_PRODUCT_COST_MODE_CONFLICT: { step: 'recipe', message: 'Elige una sola forma de llevar el costo.' },
  OTHER_SALE_PRODUCT_COST_MODE_INVALID: { step: 'recipe', message: 'Elige una sola forma de llevar el costo.' },
  OTHER_SALE_TAX_PERCENTAGE_REQUIRED: { step: 'tax', message: 'Escribe el porcentaje.' },
};

export const UNCERTAIN_CREATE_MESSAGE = 'No pudimos confirmar si el producto se creó. Revisa tus productos antes de volver a intentarlo.';
const DUPLICATE_AFTER_UNCERTAIN = 'Ya existe un producto con ese nombre. Si lo acabas de intentar crear, puede que sí se haya creado: revisa tus productos.';

/**
 * Qué decir cuando el alta no salió, y dónde. Los rechazos que son de una respuesta llevan a su paso,
 * con la explicación junto a lo que hay que cambiar (el cliente general solo sabe decir «conflicto» o
 * «sin permiso» para estos códigos). Sin red o con un error del servidor NO se afirma que falló: no se
 * sabe. Y si después de una duda el servidor dice que el nombre ya existe, lo más probable es que el
 * primer intento sí se haya creado: se dice.
 */
export function describeCreateFailure(error: unknown, fallback: string, afterUncertain: boolean): CreateFailure {
  if (error instanceof ApiError) {
    if (error.status === 0 || error.status >= 500) return { screen: 'review', message: UNCERTAIN_CREATE_MESSAGE, uncertain: true };
    const known = error.payload?.error ? STEP_BY_CODE[error.payload.error] : undefined;
    if (known) {
      const message = known.step === 'name' && afterUncertain ? DUPLICATE_AFTER_UNCERTAIN : known.message;
      return { screen: known.step, message, uncertain: false };
    }
    if (error.payload?.error === 'OTHER_SALE_PRODUCT_PERMISSION_REQUIRED') return { screen: 'review', message: 'No tienes permiso para crear productos.', uncertain: false };
    return { screen: 'review', message: error.message || fallback, uncertain: false };
  }
  return { screen: 'review', message: UNCERTAIN_CREATE_MESSAGE, uncertain: true };
}
